import React, { useState, useEffect, useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { ShowCard, CompleteSideCard } from 'src/components/uiElements/cards/ShowCard'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { useQuery } from 'react-query'
import { Box, Grid } from '@mui/material'
import { CounterCard } from 'src/components/pages/dashboard/logic/CounterCard'
import { ExchangeTypeCard } from 'src/components/pages/dashboard/logic/ExchangeTypeCard'
import AirStarlingsTicketsList from './AirStarlingsTicketsList'
import LastSalesList from './LastSalesList'
import { PERMISSION_LIST_SHOW } from 'src/helper/Permission'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import RefreshIcon from '@mui/icons-material/Refresh';
import { BILLED, CONFIRMED } from '../sales/reservation/logic/common/state'
import dayjs from 'dayjs'

export const DashboardOperationsVietur = () => {
  const translate = useTranslate()
  const [count, setCount] = useState(0)
  const [triggerLoadingStarling, setTriggerLoadingStarling] = useState(false)

  const maxCount = 999

  const pendingPayment = useQuery('pending-payment', () =>
    reservationServices.getByAllRowCounter({ item__due_date_final__gte: dayjs().startOf('year').format('YYYY-MM-DD'), 'item__due_date_final__lte': dayjs().add(15, 'day').format('YYYY-MM-DD'), billing__situation__not_in: BILLED })
  )
  const pendingVoucher = useQuery('pending-voucher', () =>
    reservationServices.getByAllRowCounter({ billing__situation__in: BILLED, tag__id__in: 228 })
  )
  const salesAutocancel = useQuery('sales-autocancel', () =>
    reservationServices.getByAllRowCounter({
      item__status__in: CONFIRMED,
      item__autocancel_date__gte: dayjs().format('YYYY-MM-DD'),
      item__autocancel_date__lte: dayjs().add(7, 'day').format('YYYY-MM-DD'),
      item__payment_status__in: 'UNPAID,PARTIALLY_PAID,NOT_REQUIRED,IN_REVIEW'
    })
  )

  const pendingConfirmation = useQuery('pending-confirmation', () =>
    reservationServices.getByAllRowCounter({
      item__status__in: 'PENDING_RESERVATION,PENDING'
    })
  )

  const incrementCount = useCallback(() => {
    setCount((prevCount) => {
      if (prevCount < maxCount) {
        return prevCount + 1
      } else {
        return 0
      }
    })
  }, [maxCount])

  useEffect(() => {
    let timer
    if (salesAutocancel.isLoading || pendingVoucher.isLoading || pendingPayment.isLoading) {
      timer = setInterval(incrementCount, 100)
    }

    return () => {
      clearInterval(timer)
    }
  }, [incrementCount, salesAutocancel.isLoading, pendingVoucher.isLoading, pendingPayment.isLoading])

  return (
    <Box style={{ paddingRight: '100px', paddingLeft: '100px' }}>
      <ExchangeTypeCard />
      <Grid container spacing={1}>
        <Grid item xs={12} md={8} lg={12} xl={8}>
          <ShowCard title={translate('ra.title.last_sales')}>
            <CompleteSideCard>
              <LastSalesList />
            </CompleteSideCard>
          </ShowCard>
          {hasPermissionFunction('air-import', PERMISSION_LIST_SHOW) && (
            <ShowCard
              title={translate('ra.title.air_tickets', {name: 'Starlings'})}
              actionButtonText={translate('ra.action.refresh_data')}
              actionButtonIcon={<RefreshIcon />}
              actionButtonLoading={triggerLoadingStarling}
              onClickButton={() => setTriggerLoadingStarling(true)}
              avoidPermission={true}
            >
              <CompleteSideCard>
                <AirStarlingsTicketsList
                  refreshData={triggerLoadingStarling}
                  handleFinishRefresh={() => setTriggerLoadingStarling(false)}
                />
              </CompleteSideCard>
            </ShowCard>
          )}
        </Grid>

        <Grid item xs={4} md={4} lg={6} xl={4}>
          <ShowCard title={translate('ra.title.pending_confirmation')}>
            <CounterCard
              count={pendingConfirmation.data === undefined ? count : pendingConfirmation.data}
              isLoading={salesAutocancel.isLoading}
              border={'20px solid #FF6188'}
              redirectTo={
                `reservation?displayedFilters=%7B"item__status__in"%3Atrue%7D&filter=%7B"item__status__in"%3A%5B"PENDING_RESERVATION"%2C"PENDING"%5D%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>

          <ShowCard title={translate('ra.title.pending_payment')}>
            <CounterCard
              count={pendingPayment.data === undefined ? count : pendingPayment.data}
              isLoading={pendingPayment.isLoading}
              border={'20px solid #78DCDC'}
              redirectTo={
                `reservation?displayedFilters=%7B"billing_situation"%3Atrue%2C"item__due_date_final"%3Atrue%7D&filter=%7B"item__due_date_final__gte"%3A"${dayjs().startOf('year').format('YYYY-MM-DD')}"%2C"item__due_date_final__lte"%3A"${dayjs().add(15, 'day').format('YYYY-MM-DD')}"%2C"billing__situation__not_in"%3A"BILLED"%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>

          <ShowCard title={translate('ra.title.pending_voucher')}>
            <CounterCard
              count={pendingVoucher.data === undefined ? count : pendingVoucher.data}
              isLoading={pendingVoucher.isLoading}
              border={'20px solid #FFD862'}
              redirectTo={
                `reservation?displayedFilters=%7B"billing_situation"%3Atrue%2C"tag__id__in"%3Atrue%7D&filter=%7B"tag__id__in"%3A%5B228%5D%2C"billing__situation__in"%3A"BILLED"%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>

          <ShowCard title={translate('ra.title.autocancel')}>
            <CounterCard
              count={salesAutocancel.data === undefined ? count : salesAutocancel.data}
              isLoading={salesAutocancel.isLoading}
              border={'20px solid #A9DC62'}
              redirectTo={
                `reservation?displayedFilters=%7B"item__status__in"%3Atrue%2C"item__autocancel_date"%3Atrue%2C"item__payment_status"%3Atrue%7D&filter=%7B"item__status__in"%3A%5B"CONFIRMED"%5D%2C"item__autocancel_date__gte"%3A"${dayjs().format('YYYY-MM-DD')}"%2C"item__autocancel_date__lte"%3A"${dayjs().add(7, 'day').format('YYYY-MM-DD')}"%2C"item__payment_status__in"%3A%5B"UNPAID"%2C"PARTIALLY_PAID"%2C"NOT_REQUIRED"%2C"IN_REVIEW"%5D%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>
        </Grid>
      </Grid>
    </Box>
  )
}
